import { useTable } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { Card, Form, Tabs } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { BuildingItem } from "./components/BuildingSearchResults";
import { EazyModelUpdate } from "./components/EazyModelUpdate";
import { EazyModelUpdateContent } from "./components/EazyModelUpdateContent";
import { EazyRegistrationDelete } from "./components/EazyRegistrationDelete";
import { EazyRegistrationDeleteContent } from "./components/EazyRegistrationDeleteContent";
import { EAZY_CONSTANTS } from "./constants";

interface SearchFormData {
  name?: string;
}

export const EazyUpdateDelete = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get("tab");

  const [updateSearchValue, setUpdateSearchValueState] = useState(searchParams.get("updateSearch") || "");
  const [deleteSearchValue, setDeleteSearchValueState] = useState(searchParams.get("deleteSearch") || "");

  const initialTab = urlTab === "registration-delete" ? "registration-delete" : "model-update";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [registeredTarget, setRegisteredTarget] = useState<BuildingItem | null>(null);
  const [switchTarget, setSwitchTarget] = useState<BuildingItem | null>(null);
  const [submitResult, setSubmitResult] = useState<"success" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedTarget, setSelectedTarget] = useState<BuildingItem | null>(null);
  const [updateSearchPerformed, setUpdateSearchPerformed] = useState(false);
  const [deleteSearchPerformed, setDeleteSearchPerformed] = useState(false);

  const {
    tableProps: updateTableProps,
    searchFormProps: updateSearchFormProps,
    tableQuery: updateTableQuery,
    setCurrent: setUpdateCurrent,
  } = useTable<BuildingItem, HttpError, SearchFormData>({
    resource: "search/buildings",
    pagination: {
      pageSize: EAZY_CONSTANTS.PAGE_SIZE,
    },
    queryOptions: {
      enabled: !!updateSearchValue && activeTab === "model-update",
      keepPreviousData: true,
    },
    filters: {
      permanent: [
        {
          field: "name",
          operator: "eq" as const,
          value: updateSearchValue,
        },
      ],
    },
    onSearch: (data) => {
      if (!data.name?.trim() && !updateSearchValue) {
        return [];
      }
      return [
        {
          field: "name",
          operator: "eq" as const,
          value: data.name?.trim() || updateSearchValue,
        },
      ];
    },
  });

  const {
    tableProps: deleteTableProps,
    searchFormProps: deleteSearchFormProps,
    tableQuery: deleteTableQuery,
    setCurrent: setDeleteCurrent,
  } = useTable<BuildingItem, HttpError, SearchFormData>({
    resource: "search/buildings",
    pagination: {
      pageSize: EAZY_CONSTANTS.PAGE_SIZE,
    },
    filters: {
      permanent: [
        {
          field: "eazy_registered",
          operator: "eq" as const,
          value: "true",
        },
        {
          field: "eazy_deleted",
          operator: "eq" as const,
          value: "true",
        },
        {
          field: "name",
          operator: "eq" as const,
          value: deleteSearchValue,
        },
      ],
    },
    queryOptions: {
      enabled: !!deleteSearchValue && activeTab === "registration-delete",
      keepPreviousData: true,
    },
    onSearch: (data) => {
      if (!data.name?.trim() && !deleteSearchValue) {
        return [];
      }
      return [
        {
          field: "name",
          operator: "eq" as const,
          value: data.name?.trim() || deleteSearchValue,
        },
      ];
    },
  });

  const [updateAllResults, setUpdateAllResults] = useState<BuildingItem[]>([]);
  const [deleteAllResults, setDeleteAllResults] = useState<BuildingItem[]>([]);
  const updatePageDataRef = useRef<Map<number, BuildingItem[]>>(new Map());
  const deletePageDataRef = useRef<Map<number, BuildingItem[]>>(new Map());
  const updateCurrentPageRef = useRef(1);
  const deleteCurrentPageRef = useRef(1);
  const lastUrlUpdateSearchRef = useRef<string>("");
  const lastUrlDeleteSearchRef = useRef<string>("");
  const lastUrlTabRef = useRef<string | null>(null);

  const updatePagination =
    updateTableProps.pagination && typeof updateTableProps.pagination === "object" ? updateTableProps.pagination : null;
  const updateCurrentPage = (updatePagination?.current as number) || 1;

  const deletePagination =
    deleteTableProps.pagination && typeof deleteTableProps.pagination === "object" ? deleteTableProps.pagination : null;
  const deleteCurrentPage = (deletePagination?.current as number) || 1;

  useEffect(() => {
    const currentUrlTab = searchParams.get("tab");
    if (currentUrlTab !== lastUrlTabRef.current) {
      lastUrlTabRef.current = currentUrlTab;
      const expectedTab = currentUrlTab === "registration-delete" ? "registration-delete" : "model-update";
      setActiveTab(expectedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    const urlUpdateSearch = searchParams.get("updateSearch") || "";
    if (urlUpdateSearch !== lastUrlUpdateSearchRef.current) {
      lastUrlUpdateSearchRef.current = urlUpdateSearch;
      setUpdateSearchValueState(urlUpdateSearch);
      updateSearchFormProps.form?.setFieldsValue({ name: urlUpdateSearch });
    }
  }, [searchParams, updateSearchFormProps.form]);

  useEffect(() => {
    const urlDeleteSearch = searchParams.get("deleteSearch") || "";
    if (urlDeleteSearch !== lastUrlDeleteSearchRef.current) {
      lastUrlDeleteSearchRef.current = urlDeleteSearch;
      setDeleteSearchValueState(urlDeleteSearch);
      deleteSearchFormProps.form?.setFieldsValue({ name: urlDeleteSearch });
    }
  }, [searchParams, deleteSearchFormProps.form]);

  const prevDeleteSearchValueRef = useRef<string>(deleteSearchValue);
  useEffect(() => {
    if (deleteSearchValue !== prevDeleteSearchValueRef.current) {
      setDeleteAllResults([]);
      deletePageDataRef.current.clear();
      deleteCurrentPageRef.current = 1;
      if (activeTab === "registration-delete" && deleteSearchValue) {
        setSelectedTarget(null);
      }
      prevDeleteSearchValueRef.current = deleteSearchValue;
    }
  }, [deleteSearchValue, activeTab]);

  useEffect(() => {
    if (activeTab === "registration-delete" && deleteTableQuery.isFetching) {
      setSelectedTarget(null);
    }
  }, [deleteTableQuery.isFetching, activeTab]);

  const prevUpdateSearchValueRef = useRef<string>(updateSearchValue);
  useEffect(() => {
    if (updateSearchValue !== prevUpdateSearchValueRef.current) {
      setUpdateAllResults([]);
      updatePageDataRef.current.clear();
      updateCurrentPageRef.current = 1;
      prevUpdateSearchValueRef.current = updateSearchValue;
    }
  }, [updateSearchValue]);

  useEffect(() => {
    if (!updateSearchValue) {
      return;
    }

    const currentDataSource = (updateTableProps.dataSource || []) as BuildingItem[];
    if (!currentDataSource || currentDataSource.length === 0) {
      return;
    }

    const currentPagination =
      updateTableProps.pagination && typeof updateTableProps.pagination === "object"
        ? updateTableProps.pagination
        : null;
    const currentPage = (currentPagination?.current as number) || 1;

    updatePageDataRef.current.set(currentPage, currentDataSource);
    updateCurrentPageRef.current = currentPage;

    const allPagesData: BuildingItem[] = [];
    const existingIds = new Set<string>();

    for (let page = 1; page <= currentPage; page++) {
      const pageData = updatePageDataRef.current.get(page);
      if (pageData) {
        for (const item of pageData) {
          if (!existingIds.has(item.building_id)) {
            existingIds.add(item.building_id);
            allPagesData.push(item);
          }
        }
      }
    }

    setUpdateAllResults(allPagesData);
  }, [updateTableProps.dataSource, updateTableProps.pagination, updateSearchValue]);

  useEffect(() => {
    if (!deleteSearchValue) {
      return;
    }

    const currentDataSource = (deleteTableProps.dataSource || []) as BuildingItem[];
    if (!currentDataSource || currentDataSource.length === 0) {
      return;
    }

    const currentPagination =
      deleteTableProps.pagination && typeof deleteTableProps.pagination === "object"
        ? deleteTableProps.pagination
        : null;
    const currentPage = (currentPagination?.current as number) || 1;

    deletePageDataRef.current.set(currentPage, currentDataSource);
    deleteCurrentPageRef.current = currentPage;

    const allPagesData: BuildingItem[] = [];
    const existingIds = new Set<string>();

    for (let page = 1; page <= currentPage; page++) {
      const pageData = deletePageDataRef.current.get(page);
      if (pageData) {
        for (const item of pageData) {
          if (!existingIds.has(item.building_id)) {
            existingIds.add(item.building_id);
            allPagesData.push(item);
          }
        }
      }
    }

    setDeleteAllResults(allPagesData);
  }, [deleteTableProps.dataSource, deleteTableProps.pagination, deleteSearchValue]);

  const updateSearchResults = updateAllResults;
  const deleteSearchResults = deleteAllResults;
  const updateHasSearched = updateSearchPerformed || updateSearchResults.length > 0 || updateTableQuery.isFetched;
  const deleteHasSearched = deleteSearchPerformed || deleteSearchResults.length > 0 || deleteTableQuery.isFetched;

  const isUpdateLoadingMore = updateTableQuery.isFetching && updateCurrentPage > 1;
  const isDeleteLoadingMore = deleteTableQuery.isFetching && deleteCurrentPage > 1;

  const handleUpdateLoadMore = useCallback(async () => {
    const total = (updatePagination?.total as number) || 0;
    const totalPages = Math.ceil(total / EAZY_CONSTANTS.PAGE_SIZE);
    const nextPage = updateCurrentPage + 1;

    if (nextPage <= totalPages) {
      setUpdateCurrent(nextPage);
    }
  }, [updateCurrentPage, updatePagination, setUpdateCurrent]);

  const handleDeleteLoadMore = useCallback(async () => {
    const total = (deletePagination?.total as number) || 0;
    const totalPages = Math.ceil(total / EAZY_CONSTANTS.PAGE_SIZE);
    const nextPage = deleteCurrentPage + 1;

    if (nextPage <= totalPages) {
      setDeleteCurrent(nextPage);
    }
  }, [deleteCurrentPage, deletePagination, setDeleteCurrent]);

  const handleUpdateRefresh = useCallback(() => {
    const searchValue = updateSearchFormProps.form?.getFieldValue("name") || "";
    if (searchValue.trim()) {
      setUpdateAllResults([]);
      updatePageDataRef.current.clear();
      updateCurrentPageRef.current = 1;

      if (updateCurrentPage !== 1) {
        setUpdateCurrent(1);
      } else {
        updateTableQuery.refetch();
      }
    }
  }, [updateSearchFormProps.form, setUpdateCurrent, updateTableQuery, updateCurrentPage]);

  const handleDeleteRefresh = useCallback(() => {
    const searchValue = deleteSearchFormProps.form?.getFieldValue("name") || "";
    if (searchValue.trim()) {
      setDeleteAllResults([]);
      deletePageDataRef.current.clear();
      deleteCurrentPageRef.current = 1;

      if (deleteCurrentPage !== 1) {
        setDeleteCurrent(1);
      } else {
        deleteTableQuery.refetch();
      }
    }
  }, [deleteSearchFormProps.form, setDeleteCurrent, deleteTableQuery, deleteCurrentPage]);

  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTab(key);
      const newSearchParams = new URLSearchParams(searchParams);
      if (key === "model-update") {
        newSearchParams.delete("tab");
      } else {
        newSearchParams.set("tab", key);
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const lastSearchedUpdateValueRef = useRef<string>("");
  const lastSearchedDeleteValueRef = useRef<string>("");

  const setUpdateSearchValue = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setUpdateSearchValueState(trimmedValue);
      const newSearchParams = new URLSearchParams(searchParams);
      if (trimmedValue) {
        newSearchParams.set("updateSearch", trimmedValue);
      } else {
        newSearchParams.delete("updateSearch");
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setDeleteSearchValue = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setDeleteSearchValueState(trimmedValue);
      const newSearchParams = new URLSearchParams(searchParams);
      if (trimmedValue) {
        newSearchParams.set("deleteSearch", trimmedValue);
      } else {
        newSearchParams.delete("deleteSearch");
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleUpdateSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      const isValueChanged = trimmedValue !== lastSearchedUpdateValueRef.current;

      setUpdateSearchPerformed(true);
      updateSearchFormProps.form?.setFieldsValue({ name: trimmedValue });
      setUpdateAllResults([]);
      updatePageDataRef.current.clear();
      updateCurrentPageRef.current = 1;
      setUpdateCurrent(1);
      lastSearchedUpdateValueRef.current = trimmedValue;

      if (!trimmedValue) {
        setUpdateSearchValue(trimmedValue);
        return;
      }

      if (isValueChanged) {
        setUpdateSearchValue(trimmedValue);
      } else {
        updateTableQuery.refetch();
      }
    },
    [updateSearchFormProps, setUpdateCurrent, updateTableQuery, setUpdateSearchValue]
  );

  const handleDeleteSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      const isValueChanged = trimmedValue !== lastSearchedDeleteValueRef.current;

      setDeleteSearchPerformed(true);
      deleteSearchFormProps.form?.setFieldsValue({ name: trimmedValue });
      setDeleteAllResults([]);
      deletePageDataRef.current.clear();
      deleteCurrentPageRef.current = 1;
      setDeleteCurrent(1);
      lastSearchedDeleteValueRef.current = trimmedValue;

      if (!trimmedValue) {
        setDeleteSearchValue(trimmedValue);
        setSelectedTarget(null);
        return;
      }

      if (isValueChanged) {
        setDeleteSearchValue(trimmedValue);
      } else {
        deleteTableQuery.refetch();
      }
      setSelectedTarget(null);
    },
    [deleteSearchFormProps, setDeleteCurrent, deleteTableQuery, setDeleteSearchValue]
  );

  return (
    <div className="flex h-full flex-col gap-6 pb-[22px]">
      <Card>
        <Tabs
          activeKey={activeTab}
          items={[
            {
              key: "model-update",
              label: "EAZY機種更新",
              children: (
                <Form {...updateSearchFormProps}>
                  <EazyModelUpdate
                    handleSearch={(value) => {
                      handleUpdateSearch(value);
                    }}
                    loading={updateTableQuery.isFetching}
                  />
                </Form>
              ),
            },
            {
              key: "registration-delete",
              label: "EAZY登録削除",
              children: (
                <Form {...deleteSearchFormProps}>
                  <EazyRegistrationDelete
                    handleSearch={(value) => {
                      handleDeleteSearch(value);
                    }}
                    loading={deleteTableQuery.isFetching}
                  />
                </Form>
              ),
            },
          ]}
          onChange={handleTabChange}
        />
      </Card>
      {activeTab === "model-update" && (
        <div className="min-h-0 flex-1">
          <EazyModelUpdateContent
            errorMessage={errorMessage}
            hasSearched={updateHasSearched}
            loadingMore={isUpdateLoadingMore}
            onErrorMessageChange={setErrorMessage}
            onLoadMore={handleUpdateLoadMore}
            onRefresh={handleUpdateRefresh}
            onRegisteredTargetChange={setRegisteredTarget}
            onSubmitResultChange={setSubmitResult}
            onSwitchTargetChange={setSwitchTarget}
            registeredTarget={registeredTarget}
            searchResults={updateSearchResults}
            submitResult={submitResult}
            switchTarget={switchTarget}
          />
        </div>
      )}
      {activeTab === "registration-delete" && (
        <div className="min-h-0 flex-1">
          <EazyRegistrationDeleteContent
            hasSearched={deleteHasSearched}
            loadingMore={isDeleteLoadingMore}
            onLoadMore={handleDeleteLoadMore}
            onRefresh={handleDeleteRefresh}
            onSelectedTargetChange={setSelectedTarget}
            searchResults={deleteSearchResults}
            selectedTarget={selectedTarget}
          />
        </div>
      )}
    </div>
  );
};
